import { useCallback, useEffect, useMemo } from "react";

import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  MenuItem,
  Switch,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import StyledLoadingButton from "../../../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import { useCreateSKU } from "../hooks/useCreateSKU";
import { useGetDevicesMakesCached } from "../../devices/hooks/useGetDevicesMakesCached";
import { useGetCustomer } from "../../../../../common/hooks/useGetCustomer";
import {
  ISKUForm,
  defaultSKUForm,
  skuCategoryOptions,
  modelLabelMap,
} from "../variables/skuData";
import { errorNotification } from "../../../../../common/variables/notification";
import { DEFAULT_DEVICE_PASSWORD } from "../../../../../common/variables/common";
import SKUFormItem from "./SKUFormItem";

const CreateSKU = (): JSX.Element => {
  const { createSKU, loading } = useCreateSKU();
  const { data, loading: deviceMakesLoading } = useGetDevicesMakesCached();
  const { customer, loading: customerLoading } = useGetCustomer();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<ISKUForm>({
    defaultValues: defaultSKUForm,
    mode: "onSubmit",
  });

  useEffect(() => {
    setValue("password", customer?.devicePassword || DEFAULT_DEVICE_PASSWORD);
  }, [customer?.devicePassword]);

  const watchedSKUData = watch("skuData");

  // Get all device types from DynamoDB (no hardcoded filter)
  const deviceTypeOptions = useMemo(() => {
    return (data?.getDevicesMakes?.items ?? [])
      .map(d => d?.model)
      .filter((model): model is string => Boolean(model))
      .sort();
  }, [data]);

  const getModelsToRunForDeviceType = useCallback(
    (deviceType: string): string[] => {
      const matched = data?.getDevicesMakes?.items?.find(
        d => d?.model === deviceType
      );

      // Filter to only include models supported by the backend
      const supportedModels = Object.keys(modelLabelMap);

      return (matched?.modelsToRun ?? []).filter(
        (model): model is string =>
          model !== null && supportedModels.includes(model)
      );
    },
    [data]
  );

  const addDevice = () => {
    const currentDevices = getValues("skuData");

    setValue("skuData", [
      ...currentDevices,
      {
        deviceType: "", // Force user to select - don't auto-select
        model: [],
      },
    ]);
  };

  const removeDevice = (indexToRemove: number) => {
    reset(prev => ({
      ...prev,
      skuData: prev.skuData.filter((_d, index) => index !== indexToRemove),
    }));
  };

  const onSubmit = async (data: ISKUForm) => {
    // Validate at least one device is added
    if (!data.skuData.length) {
      errorNotification("At least one device is required");

      return;
    }

    // Validate each device has required fields
    for (let i = 0; i < data.skuData.length; i++) {
      const device = data.skuData[i];

      if (!device.deviceType) {
        errorNotification(`Device ${i + 1}: Device Type is required`);

        return;
      }

      // Validate models - must have at least one non-empty model
      const validModels = (device.model || []).filter(
        m => m && m.trim() !== ""
      );

      if (validModels.length === 0) {
        errorNotification(`Device ${i + 1}: At least one model is required`);

        return;
      }

      // PT Type validation removed - PT Type is set at onboarding, not SKU creation
    }

    // Multiple PT device validation removed - trust OPS to create SKUs correctly

    // Validate top-level password length if provided
    if (data.password && data.password.length > 0 && data.password.length < 8) {
      errorNotification("Password must be at least 8 characters");

      return;
    }

    // Validate nodeCount
    if (!data.nodeCount || data.nodeCount < 1) {
      errorNotification("Node count must be at least 1");

      return;
    }

    const { password, hasRecordVideo, ...skuFormData } = data;

    await createSKU({
      ...skuFormData,
      nodeCount: Number(data.nodeCount),
      skuData: JSON.stringify(
        data.skuData.map(device => ({
          ...device,
          hasRecordVideo: hasRecordVideo,
          ...(password && { password }),
        }))
      ),
    });

    reset();
  };

  if (deviceMakesLoading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack marginBottom="1.5em" spacing={2} direction="row">
          <Controller
            name="skuNumber"
            control={control}
            rules={{
              required: "SKU Number is required",
            }}
            render={({ field }) => (
              <TextField
                required
                fullWidth
                label="SKU Number"
                placeholder="e.g. 001-001"
                {...field}
                value={field.value ?? ""}
                error={errors.skuNumber !== undefined}
                helperText={errors.skuNumber?.message || "SKU/Item number"}
              />
            )}
          />
          <Controller
            name="skuName"
            control={control}
            rules={{
              required: "Product Name is required",
            }}
            render={({ field }) => (
              <TextField
                required
                fullWidth
                label="Product Name"
                placeholder="e.g. Minerva System"
                {...field}
                value={field.value ?? ""}
                error={errors.skuName !== undefined}
                helperText={errors.skuName?.message || "Product name"}
              />
            )}
          />
          <Controller
            name="category"
            control={control}
            rules={{
              required: "Category is required",
            }}
            render={({ field }) => (
              <TextField
                select
                required
                fullWidth
                label="Category"
                {...field}
                value={field.value ?? ""}
                error={errors.category !== undefined}
                helperText={errors.category?.message || ""}
              >
                <MenuItem value="">
                  <em>Select a category</em>
                </MenuItem>
                {skuCategoryOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="nodeCount"
            control={control}
            rules={{
              required: "Node count is required",
              min: { value: 1, message: "Must be at least 1" },
            }}
            render={({ field }) => (
              <TextField
                required
                type="number"
                label="Node Count"
                placeholder="Number of nodes"
                {...field}
                value={field.value ?? ""}
                onChange={e => {
                  const value = e.target.value;

                  field.onChange(value === "" ? "" : parseInt(value, 10));
                }}
                error={errors.nodeCount !== undefined}
                helperText={
                  errors.nodeCount?.message || "Number of nodes in this SKU"
                }
                inputProps={{ min: 1 }}
                sx={{ minWidth: 200, maxWidth: 200 }}
              />
            )}
          />
          <Controller
            name={"password"}
            control={control}
            defaultValue={""}
            rules={{
              validate: value => {
                if (value && value.length > 0 && value.length < 8) {
                  return "Password must be at least 8 characters";
                }

                return true;
              },
            }}
            render={({ field }) => (
              <TextField
                label="Password (optional)"
                {...field}
                fullWidth
                placeholder="Device password"
                error={
                  !!errors?.password ||
                  !!(
                    field.value &&
                    field.value.length > 0 &&
                    field.value.length < 8
                  )
                }
                helperText={
                  errors?.password?.message ||
                  (field.value &&
                  field.value.length > 0 &&
                  field.value.length < 8
                    ? "Must be at least 8 characters"
                    : " ")
                }
              />
            )}
          />
          <Controller
            name="hasRecordVideo"
            control={control}
            render={({ field }) => (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: "16px" }}
              >
                <Typography variant="body2">Enable Recording</Typography>
                <Switch
                  checked={field.value ?? true}
                  onChange={e => field.onChange(e.target.checked)}
                />
              </Stack>
            )}
          />
        </Stack>

        <Stack spacing={2} marginBottom={3}>
          {watchedSKUData.map((device, index) => (
            <SKUFormItem
              key={device.deviceType + index}
              index={index}
              device={device}
              deviceTypeOptions={deviceTypeOptions}
              getModelsToRunForDeviceType={getModelsToRunForDeviceType}
              control={control}
              errors={errors}
              onRemove={removeDevice}
              watchedDeviceType={device.deviceType}
              setValue={setValue}
            />
          ))}
        </Stack>

        <Box mb={3}>
          {errors.skuData && (
            <FormHelperText error>
              <Typography variant="helperText" color="error">
                {errors.skuData.message}
              </Typography>
            </FormHelperText>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            disabled={customerLoading}
            onClick={e => {
              e.stopPropagation();

              addDevice();
            }}
          >
            Add Device
          </Button>
          <StyledLoadingButton
            disabled={loading}
            variant="contained"
            type="submit"
          >
            Create SKU
          </StyledLoadingButton>
        </Box>
      </form>
    </Box>
  );
};

export default CreateSKU;
